import { ChangeEvent, useState } from 'react';
import styled from 'styled-components';
import {
  FontSelect,
  FontSizeInput,
  FontSizeInputWrapper,
  FontSizeUnitStyle,
  handleNumberInput,
  LineSpacingSelect,
  MainLabelStyles,
  SmallLabelStyles,
  StyledSpacingWrapper,
} from 'builder/components/WidgetConfigurations/Typography/Typography';
import { FormControl, Checkbox } from '@uhg-harmony/uitk-react';
import { getCombinedFontsForTheme } from 'builder/util/getCombinedFonts';
import { ColorButtonWrapper } from 'builder/components/WidgetConfigurations/Background/Background';
import GradientColorPicker from 'builder/components/GradientColorPicker/GradientColorPicker';
import {
  StyledClose,
  StyledPickerHeader,
  StyledPickerPopover,
  StyledPickerWrapper,
} from 'builder/components/GradientColorPicker/GradientColorPicker';
import { ColorButton } from 'builder/components/ColorButton/ColorButton';
import { defaultTheme as theme } from 'shared/theme/theme';
import {
  characterSpacingValues,
  lineSpacingValues,
} from 'builder/util/constants';

const RichTextEditorGeneralPanelWrapper = styled.div`
  display: block;
  align-items: center;
`;

const StyledTextColorWrapper = styled.div`
  display: flex;
  margin-top: 1rem;
  align-items: center;
`;

const StyledWrapper = styled.div`
  display: flex;
  align-items: baseline;
  margin-top: 1rem;
`;

const ActiveHeaderColorLabel = styled.div`
  color: #1d1929;
  font-size: 14px;
  font-family: OptumSans;
  font-weight: 500;
  padding-right: 0.5rem;
  width: 13rem;
`;

const ActiveHeaderColorWrapper = styled.div`
  margin-left: -0.5rem;
  display: flex;
  margin-top: 1rem;
  align-items: center;
`;

interface RichTextEditorGeneralPanelProps<T> {
  widgetState: Widget<T>;
  disabled?: boolean;
  handleFontSize: (size: number) => void;
  handleFontFamily: (family: string) => void;
  handleTextColor: (color: string) => void;
  handleLineSpacing?: (spacing: number) => void;
  handleCharacterSpacing?: (spacing: number) => void;
  handleActiveTextColorCheckbox: (checked: boolean) => void;
  handleActiveTextColor?: (color: string) => void;
  updateSite: (site: Site) => void;
  saveSite: (site: Site) => void;
  site: Site;
}

const RichTextEditorGeneralPanel = (
  props: RichTextEditorGeneralPanelProps<any>
) => {
  const {
    widgetState,
    disabled,
    site,
    handleFontFamily,
    handleFontSize,
    handleTextColor,
    handleLineSpacing,
    handleCharacterSpacing,
    handleActiveTextColorCheckbox,
    handleActiveTextColor,
    updateSite,
    saveSite,
  } = props;

  const [fontSize, setFontSize] = useState<number>(
    widgetState.config.fontSize ?? 18
  );
  const [fontFamily, setFontFamily] = useState<string>(
    widgetState.config.fontFamily
  );
  const [textColor, setTextColor] = useState<string>(
    widgetState.config.textColor ?? '#67696d'
  );
  const [lineSpacing, setLineSpacing] = useState<number>(
    widgetState.config.lineSpacing ?? 1.5
  );
  const [characterSpacing, setCharacterSpacing] = useState<number>(
    widgetState.config.characterSpacing ?? 0
  );
  const [
    isDifferentTextColorOnActive,
    setIsDifferentTextColorOnActive,
  ] = useState<boolean>(widgetState.config.isDifferentTextColorOnActive);
  const [activeTextColor, setActiveTextColor] = useState<string>(
    widgetState.config.activeTextColor ?? 'black'
  );
  const [
    activePickerAnchorEl,
    setActivePickerAnchorEl,
  ] = useState<HTMLDivElement | null>(null);
  const activePickerOpen = Boolean(activePickerAnchorEl);
  const activePickerId = activePickerOpen
    ? 'active-text-color-picker'
    : undefined;

  const [pickerAnchorEl, setPickerAnchorEl] = useState<HTMLDivElement | null>(
    null
  );
  const [_color, setColor] = useState(widgetState.config?.iconBackgroundColor);
  const pickerOpen = Boolean(pickerAnchorEl);
  const pickerId = pickerOpen
    ? 'simple-popover-navigation-typography'
    : undefined;

  const handleFontSizeChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setFontSize(Number(e.target.value));
    handleFontSize(Number(e.target.value));
  };

  const handleFontFamilyChange = (e: ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    setFontFamily(e.target.value);
    handleFontFamily(e.target.value);
  };

  const handleTextColorChange = (color: string) => {
    setTextColor(color);
    handleTextColor(color);
  };

  const handleColorEditClick = (e: React.MouseEvent<any>) => {
    if (disabled) return;
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.id === 'active-text-color'
      ? setActivePickerAnchorEl(e.currentTarget)
      : setPickerAnchorEl(e.currentTarget);
    setColor(theme.colors.aux.skyBlue);
  };

  const handlePickerClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (disabled) return;
    e.stopPropagation();
    e.preventDefault();
  };

  const handlePickerClose = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    setPickerAnchorEl(null);
    setActivePickerAnchorEl(null);
  };

  const handleLineSpacingChange = (e: ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    const spacing = Number(e.target.value);
    setLineSpacing(spacing);
    handleLineSpacing(spacing);
  };

  const handleCharacterSpacingChange = (e: ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    const spacing = Number(e.target.value);
    setCharacterSpacing(spacing);
    handleCharacterSpacing(spacing);
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsDifferentTextColorOnActive(e.target.checked);
    handleActiveTextColorCheckbox(e.target.checked);
  };

  const handleActiveTextColorChange = (color: string) => {
    setActiveTextColor(color);
    handleActiveTextColor(color);
  };

  return (
    <RichTextEditorGeneralPanelWrapper>
      <StyledWrapper>
        <MainLabelStyles>Font</MainLabelStyles>
        <FormControl
          disabled={disabled}
          data-test-id="font-select-dropdown"
          id="controlled-select"
        >
          <FontSelect
            onChange={handleFontFamilyChange}
            value={fontFamily}
            data-test-id="font-select"
          >
            {Object.keys(getCombinedFontsForTheme()).map(f => {
              return (
                <option value={f} key={f}>
                  {f}
                </option>
              );
            })}
          </FontSelect>
        </FormControl>
        <FontSizeInputWrapper>
          <FontSizeInput
            id="typography-font-size"
            value={fontSize}
            onChange={handleFontSizeChange}
            onKeyDown={handleNumberInput}
            data-test-id="typography-font-size"
            type="number"
            min={0}
          />
          <FontSizeUnitStyle>px</FontSizeUnitStyle>
        </FontSizeInputWrapper>
      </StyledWrapper>
      <StyledWrapper>
        <MainLabelStyles>Spacing</MainLabelStyles>
        <StyledSpacingWrapper>
          <FormControl disabled={disabled} id="line-spacing-select">
            <LineSpacingSelect
              onChange={handleLineSpacingChange}
              value={lineSpacing}
              data-test-id="line-spacing-dropdown"
            >
              {lineSpacingValues.map(f => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </LineSpacingSelect>
          </FormControl>
          <SmallLabelStyles>Line</SmallLabelStyles>
        </StyledSpacingWrapper>
        <StyledSpacingWrapper>
          <FormControl disabled={disabled} id="character-spacing-select">
            <LineSpacingSelect
              onChange={handleCharacterSpacingChange}
              value={characterSpacing}
              data-test-id="character-spacing-dropdown"
            >
              {characterSpacingValues.map(f => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </LineSpacingSelect>
          </FormControl>
          <SmallLabelStyles>Character</SmallLabelStyles>
        </StyledSpacingWrapper>
      </StyledWrapper>
      <StyledTextColorWrapper>
        <MainLabelStyles>Text Color</MainLabelStyles>
        <ColorButtonWrapper
          id="text-color"
          data-test-id="text-color-icon"
          onClick={handleColorEditClick}
        >
          <ColorButton color={textColor} />
        </ColorButtonWrapper>
        <StyledPickerPopover
          data-test-id="widget-color-picker-popover"
          id={pickerId}
          open={pickerOpen}
          onClick={handlePickerClick}
          onClose={handlePickerClose}
          anchorEl={pickerAnchorEl}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
        >
          <StyledPickerWrapper>
            <StyledPickerHeader data-test-id="styled-picker-header">
              Regular Text Color Selector
            </StyledPickerHeader>
            <StyledClose
              data-test-id="close-icon"
              onClick={handlePickerClose}
            />
          </StyledPickerWrapper>
          <GradientColorPicker
            value={textColor}
            site={site}
            handleColorChange={handleTextColorChange}
            onChange={handleTextColorChange}
            updateSite={updateSite}
            saveSite={saveSite}
            anchorEl={pickerAnchorEl}
          ></GradientColorPicker>
        </StyledPickerPopover>
      </StyledTextColorWrapper>
      <ActiveHeaderColorWrapper>
        <FormControl id="active-text-color-checkbox" disabled={disabled}>
          <Checkbox
            data-test-id="active-text-color-checkbox"
            onChange={handleCheckboxChange}
            checked={isDifferentTextColorOnActive}
          />
        </FormControl>
        <ActiveHeaderColorLabel>
          Different Text Color for Active Header?
        </ActiveHeaderColorLabel>
        {isDifferentTextColorOnActive && (
          <>
            <ColorButtonWrapper
              id="active-text-color"
              data-test-id="active-text-color-icon"
              onClick={handleColorEditClick}
            >
              <ColorButton color={activeTextColor ?? 'black'} />
            </ColorButtonWrapper>
            <StyledPickerPopover
              data-test-id="widget-color-picker-popover"
              id={activePickerId}
              open={activePickerOpen}
              onClick={handlePickerClick}
              onClose={handlePickerClose}
              anchorEl={activePickerAnchorEl}
              anchorOrigin={{
                vertical: 'center',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'center',
                horizontal: 'right',
              }}
            >
              <StyledPickerWrapper>
                <StyledPickerHeader data-test-id="styled-picker-header">
                  Active Text Color Selector
                </StyledPickerHeader>
                <StyledClose
                  data-test-id="close-icon"
                  onClick={handlePickerClose}
                />
              </StyledPickerWrapper>
              <GradientColorPicker
                value={activeTextColor ?? 'black'}
                site={site}
                handleColorChange={handleActiveTextColorChange}
                onChange={handleActiveTextColorChange}
                updateSite={updateSite}
                saveSite={saveSite}
                anchorEl={activePickerAnchorEl}
              />
            </StyledPickerPopover>
          </>
        )}
      </ActiveHeaderColorWrapper>
    </RichTextEditorGeneralPanelWrapper>
  );
};

export default RichTextEditorGeneralPanel;
